/**
 * Pick the saved Rx that belongs to one of the Rx we submitted.
 *
 * `patientSaveRxs` answers with one entry **per therapy line**, not per Rx: a
 * Kassenrezept with two Heilmittel comes back as two entries carrying the same
 * `id`. Counting entries is therefore not a way to tell "one Rx" from "several"
 * — which is what MD-383 turned on. A newly created Rx has no id on the way in
 * (the server generates it), so there was nothing to match on either, and the
 * payer read off the saved Rx came back undefined while Versichertennummer and
 * Status survived on their form fallback. The Kostenträger line went missing
 * from the liftover dialog and the payer was never copied to the patient.
 *
 * So: match on the id when we have one, and otherwise accept the response as
 * long as it describes a single Rx, however many lines it arrived as.
 *
 * Returns undefined when the response covers several *different* Rx and there
 * is no id to disambiguate — guessing there would copy one Rx's insurance data
 * onto the patient off the back of another.
 */
export const selectSavedRx = <T extends { id?: string }>(
  savedRxs: readonly T[],
  inputRx: { id?: string },
): T | undefined => {
  if (inputRx.id) {
    const byId = savedRxs.find((rx) => rx.id === inputRx.id);
    if (byId) return byId;
  }

  const distinctIds = new Set(savedRxs.map((rx) => rx.id));
  return distinctIds.size === 1 ? savedRxs[0] : undefined;
};
